import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import * as Icon from 'react-feather';
import MainStructure from '../../components/general/MainStructure';
import TimezoneSelector from '../components/TimezoneSelector';

/* ─────────────────────────────────────────
   Shared style tokens
───────────────────────────────────────── */
const CARD = {
  background: '#fff',
  border: '1px solid #e8edf2',
  borderRadius: 12,
  padding: 24,
};

const BTN_PRIMARY = {
  background: '#4f46e5',
  color: '#fff',
  border: 'none',
  borderRadius: 9,
  padding: '9px 22px',
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 7,
};

const BTN_OUTLINE = {
  background: '#fff',
  color: '#4f46e5',
  border: '1.5px solid #4f46e5',
  borderRadius: 9,
  padding: '9px 22px',
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 7,
};

const BTN_DISABLED = {
  ...BTN_PRIMARY,
  background: '#cbd5e1',
  cursor: 'not-allowed',
};

/* ─────────────────────────────────────────
   Step Indicator
───────────────────────────────────────── */
const STEPS = ['Upload', 'Group', 'Captions', 'Schedule', 'Review'];

function StepIndicator({ current }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: 28,
        padding: '16px 24px',
        background: '#fff',
        border: '1px solid #e8edf2',
        borderRadius: 12,
        overflowX: 'auto',
      }}
    >
      {STEPS.map((label, idx) => {
        const stepNum = idx + 1;
        const isDone = stepNum < current;
        const isActive = stepNum === current;
        const isFuture = stepNum > current;

        const circleStyle = {
          width: 32,
          height: 32,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: 13,
          flexShrink: 0,
          ...(isDone
            ? { background: '#4f46e5', color: '#fff', border: 'none' }
            : isActive
            ? { background: '#fff', color: '#4f46e5', border: '2.5px solid #4f46e5' }
            : { background: '#f1f5f9', color: '#94a3b8', border: '2px solid #e2e8f0' }),
        };

        return (
          <React.Fragment key={label}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 64 }}>
              <div style={circleStyle}>
                {isDone ? <Icon.Check size={14} strokeWidth={3} /> : stepNum}
              </div>
              <span
                style={{
                  marginTop: 5,
                  fontSize: 11,
                  fontWeight: isActive ? 700 : 500,
                  color: isDone || isActive ? '#4f46e5' : '#94a3b8',
                  whiteSpace: 'nowrap',
                }}
              >
                {label}
              </span>
            </div>
            {idx < STEPS.length - 1 && (
              <div
                style={{
                  flex: 1,
                  height: 2,
                  margin: '0 6px',
                  marginBottom: 18,
                  background: isDone ? '#4f46e5' : '#e2e8f0',
                  minWidth: 20,
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ─────────────────────────────────────────
   Helpers
───────────────────────────────────────── */
function formatDatetime(dateStr, timeStr) {
  if (!dateStr) return '—';
  const [h, m] = (timeStr || '12:00').split(':').map(Number);
  const d = new Date(dateStr + 'T00:00:00');
  d.setHours(h, m, 0, 0);
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

function addHours(dateStr, timeStr, hours) {
  if (!dateStr) return '—';
  const [h, m] = (timeStr || '12:00').split(':').map(Number);
  const d = new Date(dateStr + 'T00:00:00');
  d.setHours(h + hours, m, 0, 0);
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

function toApiDatetime(dateStr, timeStr, extraHours = 0) {
  if (!dateStr) return '';
  const [h, m] = (timeStr || '12:00').split(':').map(Number);
  const d = new Date(dateStr + 'T00:00:00');
  d.setHours(h + extraHours, m, 0, 0);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
}

function todayISO() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/* ─────────────────────────────────────────
   Step 1 — Upload Images
───────────────────────────────────────── */
function StepUpload({ uploadedImages, setUploadedImages, onNext }) {
  const [uploading, setUploading] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);

  const uploadImage = useCallback(async (file) => {
    setUploadingCount((c) => c + 1);
    const formData = new FormData();
    formData.append('image', file);
    try {
      const response = await axios.post(
        'https://api.schedpilot.com/wp-json/custom/v1/upload-image',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
        }
      );
      if (response.data.code === 200) {
        setUploadedImages((prev) => [
          ...prev,
          { url: response.data.file_url, id: response.data.image_id },
        ]);
      }
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setUploadingCount((c) => {
        const next = c - 1;
        if (next === 0) setUploading(false);
        return next;
      });
    }
  }, [setUploadedImages]);

  const onDrop = useCallback(
    (acceptedFiles) => {
      if (acceptedFiles.length === 0) return;
      setUploading(true);
      acceptedFiles.forEach((file) => uploadImage(file));
    },
    [uploadImage]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    accept: { 'image/*': [] },
  });

  const removeImage = (id) => {
    setUploadedImages((prev) => prev.filter((img) => img.id !== id));
  };

  const canNext = uploadedImages.length >= 2;

  return (
    <div>
      <div className="mb-3">
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>
          Upload Images
        </h2>
        <p style={{ fontSize: 13, color: '#64748b', marginBottom: 0 }}>
          Upload all images you want to use across your carousel posts. You need at least 2.
        </p>
      </div>

      {/* Drop zone */}
      <div
        {...getRootProps()}
        style={{
          border: `2px dashed ${isDragActive ? '#4f46e5' : '#cbd5e1'}`,
          borderRadius: 12,
          padding: '48px 24px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: isDragActive ? '#eef2ff' : '#f8fafc',
          cursor: 'pointer',
          transition: 'all 0.15s',
          marginBottom: 20,
          minHeight: 180,
        }}
      >
        <input {...getInputProps()} />
        {uploading ? (
          <>
            <div
              className="spinner-border"
              style={{ color: '#4f46e5', width: 32, height: 32, marginBottom: 12 }}
              role="status"
            />
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 0 }}>
              Uploading {uploadingCount} image{uploadingCount !== 1 ? 's' : ''}…
            </p>
          </>
        ) : (
          <>
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: 12,
                background: isDragActive ? '#c7d2fe' : '#e2e8f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 14,
              }}
            >
              <Icon.Upload size={22} color={isDragActive ? '#4f46e5' : '#64748b'} />
            </div>
            <p style={{ fontSize: 15, fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>
              {isDragActive ? 'Drop images here' : 'Drag & drop images here or click to browse'}
            </p>
            <p style={{ fontSize: 12, color: '#94a3b8', marginBottom: 0 }}>
              PNG, JPG, WEBP, GIF accepted
            </p>
          </>
        )}
      </div>

      {/* Uploaded images grid */}
      {uploadedImages.length > 0 && (
        <div>
          <p style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {uploadedImages.length} image{uploadedImages.length !== 1 ? 's' : ''} uploaded
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
              gap: 10,
            }}
          >
            {uploadedImages.map((img) => (
              <div key={img.id} style={{ position: 'relative', borderRadius: 8, overflow: 'hidden' }}>
                <img
                  src={img.url}
                  alt=""
                  style={{ width: '100%', height: 90, objectFit: 'cover', display: 'block' }}
                />
                <button
                  onClick={() => removeImage(img.id)}
                  style={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    background: 'rgba(15,23,42,0.7)',
                    border: 'none',
                    borderRadius: '50%',
                    width: 22,
                    height: 22,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    padding: 0,
                  }}
                >
                  <Icon.X size={12} color="#fff" strokeWidth={3} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Nav */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 28 }}>
        <button
          onClick={onNext}
          disabled={!canNext}
          style={canNext ? BTN_PRIMARY : BTN_DISABLED}
        >
          Next <Icon.ArrowRight size={14} strokeWidth={2.5} />
        </button>
      </div>

      {!canNext && uploadedImages.length > 0 && (
        <p style={{ fontSize: 12, color: '#f59e0b', textAlign: 'right', marginTop: 6 }}>
          Upload at least {2 - uploadedImages.length} more image{2 - uploadedImages.length !== 1 ? 's' : ''} to continue
        </p>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────
   Step 2 — Group into Posts
───────────────────────────────────────── */
function StepGroup({ uploadedImages, groups, setGroups, onBack, onNext }) {
  const [draggingId, setDraggingId] = useState(null);
  const [draggingFrom, setDraggingFrom] = useState(null); // 'ungrouped' | groupId
  const [hoveredGroup, setHoveredGroup] = useState(null);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editingName, setEditingName] = useState('');

  // All imageIds that are in a group
  const groupedIds = new Set(groups.flatMap((g) => g.imageIds));
  const ungrouped = uploadedImages.filter((img) => !groupedIds.has(img.id));

  const handleDragStart = (e, imageId, from) => {
    setDraggingId(imageId);
    setDraggingFrom(from);
    e.dataTransfer.setData('imageId', imageId);
    e.dataTransfer.setData('fromGroup', from);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDrop = (e, targetGroupId) => {
    e.preventDefault();
    const imageId = Number(e.dataTransfer.getData('imageId'));
    const fromGroup = e.dataTransfer.getData('fromGroup');

    if (!imageId) return;

    setGroups((prev) => {
      let updated = prev.map((g) => {
        if (fromGroup !== 'ungrouped' && String(g.id) === String(fromGroup)) {
          return { ...g, imageIds: g.imageIds.filter((id) => id !== imageId) };
        }
        return g;
      });
      if (targetGroupId !== 'ungrouped') {
        updated = updated.map((g) => {
          if (String(g.id) === String(targetGroupId)) {
            if (g.imageIds.includes(imageId)) return g;
            return { ...g, imageIds: [...g.imageIds, imageId] };
          }
          return g;
        });
      }
      return updated;
    });

    setDraggingId(null);
    setDraggingFrom(null);
    setHoveredGroup(null);
  };

  const handleDropUngrouped = (e) => {
    e.preventDefault();
    const imageId = Number(e.dataTransfer.getData('imageId'));
    const fromGroup = e.dataTransfer.getData('fromGroup');
    if (!imageId || fromGroup === 'ungrouped') return;

    setGroups((prev) =>
      prev.map((g) => {
        if (String(g.id) === String(fromGroup)) {
          return { ...g, imageIds: g.imageIds.filter((id) => id !== imageId) };
        }
        return g;
      })
    );
    setDraggingId(null);
    setDraggingFrom(null);
    setHoveredGroup(null);
  };

  const removeFromGroup = (groupId, imageId) => {
    setGroups((prev) =>
      prev.map((g) =>
        g.id === groupId ? { ...g, imageIds: g.imageIds.filter((id) => id !== imageId) } : g
      )
    );
  };

  const addGroup = () => {
    const newId = 'g' + Date.now();
    setGroups((prev) => [...prev, { id: newId, name: `Post ${prev.length + 1}`, imageIds: [] }]);
  };

  const removeGroup = (groupId) => {
    setGroups((prev) => prev.filter((g) => g.id !== groupId));
  };

  const startRename = (g) => {
    setEditingGroupId(g.id);
    setEditingName(g.name);
  };

  const commitRename = () => {
    if (editingName.trim()) {
      setGroups((prev) =>
        prev.map((g) => (g.id === editingGroupId ? { ...g, name: editingName.trim() } : g))
      );
    }
    setEditingGroupId(null);
  };

  // Instagram carousels take 2 to 10 items.
  const allValid = groups.length > 0 && groups.every((g) => g.imageIds.length >= 2 && g.imageIds.length <= 10);
  const hasUngrouped = ungrouped.length > 0;
  const canNext = allValid; // allow even with ungrouped (show warning)

  const imgById = Object.fromEntries(uploadedImages.map((img) => [img.id, img]));

  return (
    <div>
      <div className="mb-3">
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>
          Group Images into Posts
        </h2>
        <p style={{ fontSize: 13, color: '#64748b', marginBottom: 0 }}>
          Drag images from the left into post groups on the right. Each post needs at least 2 images.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        {/* Left — Ungrouped */}
        <div
          style={{
            width: '30%',
            minWidth: 140,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              ...CARD,
              minHeight: 200,
              transition: 'border-color 0.15s',
              borderColor: hoveredGroup === 'ungrouped' ? '#4f46e5' : '#e8edf2',
              background: hoveredGroup === 'ungrouped' ? '#eef2ff' : '#fff',
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setHoveredGroup('ungrouped');
            }}
            onDragLeave={() => setHoveredGroup(null)}
            onDrop={handleDropUngrouped}
          >
            <p style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Ungrouped ({ungrouped.length})
            </p>
            {ungrouped.length === 0 ? (
              <p style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', marginTop: 24 }}>
                All images grouped!
              </p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {ungrouped.map((img) => (
                  <div
                    key={img.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, img.id, 'ungrouped')}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 6,
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: 8,
                      padding: '8px',
                      cursor: 'grab',
                      opacity: draggingId === img.id ? 0.4 : 1,
                    }}
                  >
                    <img
                      src={img.url}
                      alt=""
                      style={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 6, flexShrink: 0, display: 'block' }}
                    />
                    <Icon.Move size={12} color="#94a3b8" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right — Groups */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {groups.map((group) => {
            const isOver = hoveredGroup === group.id;
            const insufficient = group.imageIds.length < 2 && group.imageIds.length > 0;
            const tooMany = group.imageIds.length > 10;
            const empty = group.imageIds.length === 0;
            return (
              <div
                key={group.id}
                style={{
                  ...CARD,
                  borderColor: isOver ? '#4f46e5' : insufficient || tooMany ? '#f59e0b' : '#e8edf2',
                  background: isOver ? '#eef2ff' : '#fff',
                  transition: 'border-color 0.15s, background 0.15s',
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  setHoveredGroup(group.id);
                }}
                onDragLeave={() => setHoveredGroup(null)}
                onDrop={(e) => handleDrop(e, group.id)}
              >
                {/* Group header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  {editingGroupId === group.id ? (
                    <input
                      autoFocus
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onBlur={commitRename}
                      onKeyDown={(e) => e.key === 'Enter' && commitRename()}
                      style={{
                        flex: 1,
                        border: '1.5px solid #4f46e5',
                        borderRadius: 6,
                        padding: '4px 8px',
                        fontSize: 13,
                        fontWeight: 700,
                        color: '#0f172a',
                        outline: 'none',
                      }}
                    />
                  ) : (
                    <button
                      onClick={() => startRename(group)}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        fontSize: 14,
                        fontWeight: 700,
                        color: '#0f172a',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 5,
                        flex: 1,
                      }}
                      title="Click to rename"
                    >
                      {group.name}
                      <Icon.Edit2 size={12} color="#94a3b8" />
                    </button>
                  )}
                  {groups.length > 1 && (
                    <button
                      onClick={() => removeGroup(group.id)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}
                      title="Remove group"
                    >
                      <Icon.Trash2 size={14} color="#ef4444" />
                    </button>
                  )}
                </div>

                {/* Images in group */}
                {empty && !isOver && (
                  <div
                    style={{
                      border: '2px dashed #e2e8f0',
                      borderRadius: 8,
                      padding: '20px 16px',
                      textAlign: 'center',
                      color: '#94a3b8',
                      fontSize: 12,
                    }}
                  >
                    Drop images here
                  </div>
                )}

                {group.imageIds.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {group.imageIds.map((imgId) => {
                      const img = imgById[imgId];
                      if (!img) return null;
                      return (
                        <div
                          key={imgId}
                          draggable
                          onDragStart={(e) => handleDragStart(e, imgId, group.id)}
                          style={{
                            position: 'relative',
                            borderRadius: 6,
                            overflow: 'hidden',
                            cursor: 'grab',
                            opacity: draggingId === imgId ? 0.4 : 1,
                          }}
                        >
                          <img
                            src={img.url}
                            alt=""
                            style={{ width: 110, height: 110, objectFit: 'cover', display: 'block' }}
                          />
                          <button
                            onClick={() => removeFromGroup(group.id, imgId)}
                            style={{
                              position: 'absolute',
                              top: 2,
                              right: 2,
                              background: 'rgba(15,23,42,0.65)',
                              border: 'none',
                              borderRadius: '50%',
                              width: 18,
                              height: 18,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              padding: 0,
                            }}
                          >
                            <Icon.X size={10} color="#fff" strokeWidth={3} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {insufficient && (
                  <p style={{ fontSize: 11, color: '#f59e0b', marginTop: 8, marginBottom: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Icon.AlertTriangle size={11} /> Needs at least 2 images
                  </p>
                )}
                {tooMany && (
                  <p style={{ fontSize: 11, color: '#f59e0b', marginTop: 8, marginBottom: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Icon.AlertTriangle size={11} /> Instagram allows at most 10 images — move {group.imageIds.length - 10} to another carousel
                  </p>
                )}
              </div>
            );
          })}

          <button
            onClick={addGroup}
            style={{
              background: '#f8fafc',
              border: '2px dashed #cbd5e1',
              borderRadius: 12,
              padding: '12px 20px',
              color: '#64748b',
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              transition: 'border-color 0.15s',
            }}
          >
            <Icon.Plus size={15} strokeWidth={2.5} /> Add another post
          </button>
        </div>
      </div>

      {/* Warnings */}
      {hasUngrouped && (
        <div
          style={{
            marginTop: 14,
            background: '#fffbeb',
            border: '1px solid #fde68a',
            borderRadius: 8,
            padding: '10px 14px',
            fontSize: 12,
            color: '#92400e',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <Icon.AlertTriangle size={13} />
          {ungrouped.length} image{ungrouped.length !== 1 ? 's are' : ' is'} still ungrouped. They will not be included in any post.
        </div>
      )}

      {/* Nav */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
        <button onClick={onBack} style={BTN_OUTLINE}>
          <Icon.ArrowLeft size={14} strokeWidth={2.5} /> Back
        </button>
        <button onClick={onNext} disabled={!canNext} style={canNext ? BTN_PRIMARY : BTN_DISABLED}>
          Next <Icon.ArrowRight size={14} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   Step 3 — Write Captions
───────────────────────────────────────── */
function StepCaptions({ uploadedImages, groups, captions, setCaptions, onBack, onNext }) {
  const [activeTab, setActiveTab] = useState(groups[0]?.id || null);
  const imgById = Object.fromEntries(uploadedImages.map((img) => [img.id, img]));

  const getCaption = (groupId) => captions[groupId]?.caption || '';
  const getSlideText = (groupId, imgId) => captions[groupId]?.slides?.[imgId] || '';

  const setCaption = (groupId, value) => {
    setCaptions((prev) => ({
      ...prev,
      [groupId]: { ...prev[groupId], caption: value },
    }));
  };

  const setSlideText = (groupId, imgId, value) => {
    setCaptions((prev) => ({
      ...prev,
      [groupId]: {
        ...prev[groupId],
        slides: { ...(prev[groupId]?.slides || {}), [imgId]: value },
      },
    }));
  };

  const activeGroup = groups.find((g) => g.id === activeTab);

  return (
    <div>
      <div className="mb-3">
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>
          Write Captions
        </h2>
        <p style={{ fontSize: 13, color: '#64748b', marginBottom: 0 }}>
          Add a post caption and optional overlay text for each slide.
        </p>
      </div>

      {/* Tabs */}
      <div
        style={{
          display: 'flex',
          gap: 4,
          borderBottom: '2px solid #e8edf2',
          marginBottom: 20,
          overflowX: 'auto',
        }}
      >
        {groups.map((g) => (
          <button
            key={g.id}
            onClick={() => setActiveTab(g.id)}
            style={{
              background: 'none',
              border: 'none',
              padding: '10px 16px',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              borderBottom: activeTab === g.id ? '2px solid #4f46e5' : '2px solid transparent',
              color: activeTab === g.id ? '#4f46e5' : '#64748b',
              marginBottom: -2,
              whiteSpace: 'nowrap',
              transition: 'color 0.15s',
            }}
          >
            {g.name}
          </button>
        ))}
      </div>

      {/* Active group content */}
      {activeGroup && (
        <div>
          {/* Post Caption */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 6 }}>
              Post Caption
            </label>
            <textarea
              value={getCaption(activeGroup.id)}
              onChange={(e) => setCaption(activeGroup.id, e.target.value)}
              placeholder="Write the main Instagram caption for this post…"
              style={{
                width: '100%',
                minHeight: 100,
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                padding: '10px 12px',
                fontSize: 13,
                color: '#0f172a',
                resize: 'vertical',
                outline: 'none',
                fontFamily: 'inherit',
                lineHeight: 1.6,
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Slides */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 10 }}>
              Slide Overlay Text (optional)
            </label>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(6, 1fr)',
                gap: 14,
              }}
            >
              {activeGroup.imageIds.map((imgId, idx) => {
                const img = imgById[imgId];
                if (!img) return null;
                return (
                  <div
                    key={imgId}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                    }}
                  >
                    <div style={{ position: 'relative' }}>
                      <img
                        src={img.url}
                        alt={`Slide ${idx + 1}`}
                        style={{
                          width: '100%',
                          aspectRatio: '4 / 5',
                          objectFit: 'cover',
                          borderRadius: 8,
                          display: 'block',
                          border: '1px solid #e2e8f0',
                        }}
                      />
                      <span
                        style={{
                          position: 'absolute',
                          bottom: 7,
                          left: 7,
                          background: 'rgba(15,23,42,0.65)',
                          color: '#fff',
                          fontSize: 11,
                          fontWeight: 700,
                          borderRadius: 4,
                          padding: '2px 7px',
                        }}
                      >
                        {idx + 1}
                      </span>
                    </div>
                    <textarea
                      value={getSlideText(activeGroup.id, imgId)}
                      onChange={(e) => setSlideText(activeGroup.id, imgId, e.target.value)}
                      placeholder="Overlay text…"
                      style={{
                        width: '100%',
                        height: 68,
                        border: '1px solid #e2e8f0',
                        borderRadius: 7,
                        padding: '7px 9px',
                        fontSize: 12,
                        color: '#0f172a',
                        resize: 'none',
                        outline: 'none',
                        fontFamily: 'inherit',
                        lineHeight: 1.5,
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Nav */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 28 }}>
        <button onClick={onBack} style={BTN_OUTLINE}>
          <Icon.ArrowLeft size={14} strokeWidth={2.5} /> Back
        </button>
        <button onClick={onNext} style={BTN_PRIMARY}>
          Next <Icon.ArrowRight size={14} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   Step 4 — Schedule & Accounts
───────────────────────────────────────── */
const INTERVALS = [
  { label: '1h', value: 1 },
  { label: '3h', value: 3 },
  { label: '6h', value: 6 },
  { label: '12h', value: 12 },
  { label: '24h', value: 24 },
  { label: '48h', value: 48 },
  { label: '72h', value: 72 },
];

function StepSchedule({
  groups,
  instagramAccounts, setInstagramAccounts,
  selectedAccounts, setSelectedAccounts,
  startDate, setStartDate,
  startTime, setStartTime,
  intervalHours, setIntervalHours,
  timezone, setTimezone,
  onBack, onNext,
}) {
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [accountsError, setAccountsError] = useState('');

  useEffect(() => {
    setLoadingAccounts(true);
    fetch('https://api.schedpilot.com/wp-json/api/v1/instagram-accounts', {
      credentials: 'include',
    })
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setInstagramAccounts(data);
        } else if (data && Array.isArray(data.accounts)) {
          setInstagramAccounts(data.accounts);
        } else {
          setInstagramAccounts([]);
        }
      })
      .catch(() => {
        setAccountsError('Could not load Instagram accounts.');
        setInstagramAccounts([]);
      })
      .finally(() => setLoadingAccounts(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleAccount = (id) => {
    setSelectedAccounts((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]
    );
  };

  const canNext = selectedAccounts.length >= 1 && startDate;

  return (
    <div>
      <div className="mb-3">
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>
          Schedule & Accounts
        </h2>
        <p style={{ fontSize: 13, color: '#64748b', marginBottom: 0 }}>
          Choose which Instagram accounts to post to and set your schedule.
        </p>
      </div>

      {/* Accounts section */}
      <div style={{ ...CARD, marginBottom: 16 }}>
        <p style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 14 }}>
          Instagram Accounts
        </p>

        {loadingAccounts && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#64748b', fontSize: 13 }}>
            <div className="spinner-border spinner-border-sm" style={{ color: '#4f46e5' }} role="status" />
            Loading accounts…
          </div>
        )}

        {!loadingAccounts && accountsError && (
          <p style={{ fontSize: 13, color: '#ef4444' }}>{accountsError}</p>
        )}

        {!loadingAccounts && !accountsError && instagramAccounts.length === 0 && (
          <p style={{ fontSize: 13, color: '#64748b' }}>
            No Instagram accounts connected.{' '}
            <a href="/social-accounts" style={{ color: '#4f46e5', fontWeight: 600 }}>
              Go to Accounts
            </a>{' '}
            to connect one.
          </p>
        )}

        {!loadingAccounts && instagramAccounts.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {instagramAccounts.map((acc) => {
              const selected = selectedAccounts.includes(acc.id);
              return (
                <div
                  key={acc.id}
                  onClick={() => toggleAccount(acc.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    border: selected ? '2px solid #4f46e5' : '2px solid #e2e8f0',
                    borderRadius: 10,
                    padding: '10px 14px',
                    cursor: 'pointer',
                    background: selected ? '#eef2ff' : '#fff',
                    transition: 'all 0.15s',
                    minWidth: 160,
                  }}
                >
                  {acc.avatar ? (
                    <img
                      src={acc.avatar}
                      alt={acc.name}
                      style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        background: selected ? '#c7d2fe' : '#e2e8f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: 14,
                        color: selected ? '#4f46e5' : '#64748b',
                      }}
                    >
                      {(acc.name || acc.username || '?')[0].toUpperCase()}
                    </div>
                  )}
                  <div>
                    <p style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', margin: 0 }}>
                      {acc.name || acc.username}
                    </p>
                    {acc.username && acc.name && (
                      <p style={{ fontSize: 11, color: '#64748b', margin: 0 }}>@{acc.username}</p>
                    )}
                  </div>
                  {selected && (
                    <Icon.CheckCircle size={16} color="#4f46e5" style={{ marginLeft: 'auto' }} />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Schedule section */}
      <div style={{ ...CARD, marginBottom: 16 }}>
        <p style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 14 }}>
          Schedule
        </p>

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 18 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              min={todayISO()}
              onChange={(e) => setStartDate(e.target.value)}
              style={{
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                padding: '8px 12px',
                fontSize: 13,
                color: '#0f172a',
                outline: 'none',
              }}
            />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>
              Start Time
            </label>
            <input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              style={{
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                padding: '8px 12px',
                fontSize: 13,
                color: '#0f172a',
                outline: 'none',
              }}
            />
          </div>
        </div>

        {/* Interval buttons */}
        <div style={{ marginBottom: 18 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 8 }}>
            Interval Between Posts
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {INTERVALS.map(({ label, value }) => {
              const active = intervalHours === value;
              return (
                <button
                  key={value}
                  onClick={() => setIntervalHours(value)}
                  style={{
                    padding: '7px 16px',
                    borderRadius: 8,
                    border: active ? '2px solid #4f46e5' : '1.5px solid #e2e8f0',
                    background: active ? '#eef2ff' : '#f8fafc',
                    color: active ? '#4f46e5' : '#64748b',
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Timezone */}
        <div style={{ marginBottom: 18 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
            Timezone
          </label>
          <TimezoneSelector value={timezone} onChange={setTimezone} label="" className="" />
          <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 5, marginBottom: 0 }}>
            Posts will be scheduled in this timezone.
          </p>
        </div>

        {/* Schedule preview */}
        {startDate && (
          <div>
            <p style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>
              Schedule Preview
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {groups.map((g, idx) => (
                <div
                  key={g.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    fontSize: 13,
                    color: '#374151',
                  }}
                >
                  <span
                    style={{
                      background: '#eef2ff',
                      color: '#4f46e5',
                      borderRadius: 5,
                      padding: '2px 8px',
                      fontWeight: 700,
                      fontSize: 12,
                      flexShrink: 0,
                    }}
                  >
                    {g.name}
                  </span>
                  <Icon.ArrowRight size={12} color="#94a3b8" />
                  <span style={{ color: '#374151' }}>
                    {idx === 0
                      ? formatDatetime(startDate, startTime)
                      : addHours(startDate, startTime, idx * intervalHours)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Nav */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
        <button onClick={onBack} style={BTN_OUTLINE}>
          <Icon.ArrowLeft size={14} strokeWidth={2.5} /> Back
        </button>
        <button onClick={onNext} disabled={!canNext} style={canNext ? BTN_PRIMARY : BTN_DISABLED}>
          Next <Icon.ArrowRight size={14} strokeWidth={2.5} />
        </button>
      </div>
      {!canNext && (
        <p style={{ fontSize: 12, color: '#f59e0b', textAlign: 'right', marginTop: 6 }}>
          {!selectedAccounts.length ? 'Select at least one account. ' : ''}
          {!startDate ? 'Set a start date.' : ''}
        </p>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────
   Step 5 — Review & Create
───────────────────────────────────────── */
function StepReview({
  uploadedImages, groups, captions,
  selectedAccounts, instagramAccounts,
  startDate, startTime, intervalHours, timezone,
  creating, setCreating,
  onBack,
}) {
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const imgById = Object.fromEntries(uploadedImages.map((img) => [img.id, img]));
  const accountById = Object.fromEntries(instagramAccounts.map((a) => [a.id, a]));

  const handleCreate = async () => {
    setCreating(true);
    setError('');

    const posts = groups.map((g) => {
      const groupCaptions = captions[g.id] || {};
      const slides = g.imageIds.map((imgId, idx) => ({
        image_id: imgId,
        text: groupCaptions.slides?.[imgId] || '',
        position: idx,
      }));
      return {
        caption: groupCaptions.caption || '',
        slides,
      };
    });

    const body = {
      posts,
      account_ids: selectedAccounts,
      start_datetime: toApiDatetime(startDate, startTime, 0),
      interval_hours: intervalHours,
      timezone: timezone,
    };

    try {
      const res = await fetch(
        'https://api.schedpilot.com/wp-json/api/v1/create-carousel-batch',
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }
      );
      const data = await res.json();
      if (res.ok && (data.code === 200 || data.success || res.status === 200)) {
        setSuccess(true);
      } else {
        setError(data.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setCreating(false);
    }
  };

  if (success) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: '#dcfce7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
          }}
        >
          <Icon.CheckCircle size={32} color="#16a34a" />
        </div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>
          Posts Created!
        </h2>
        <p style={{ fontSize: 14, color: '#64748b', marginBottom: 24 }}>
          {groups.length} carousel post{groups.length !== 1 ? 's have' : ' has'} been scheduled successfully.
        </p>
        <button onClick={() => navigate('/creator-studio')} style={BTN_PRIMARY}>
          <Icon.ArrowLeft size={14} strokeWidth={2.5} /> Back to Creator Studio
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3">
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>
          Review & Create
        </h2>
        <p style={{ fontSize: 13, color: '#64748b', marginBottom: 0 }}>
          Check everything looks right before scheduling your posts.
        </p>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
        {groups.map((g, idx) => {
          const firstImgId = g.imageIds[0];
          const firstImg = firstImgId ? imgById[firstImgId] : null;
          const scheduledAt =
            idx === 0
              ? formatDatetime(startDate, startTime)
              : addHours(startDate, startTime, idx * intervalHours);

          return (
            <div
              key={g.id}
              style={{
                ...CARD,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 16,
              }}
            >
              {/* Thumbnail */}
              {firstImg ? (
                <img
                  src={firstImg.url}
                  alt={g.name}
                  style={{
                    width: 64,
                    height: 64,
                    objectFit: 'cover',
                    borderRadius: 8,
                    flexShrink: 0,
                    border: '1px solid #e2e8f0',
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 64,
                    height: 64,
                    background: '#f1f5f9',
                    borderRadius: 8,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon.Image size={20} color="#94a3b8" />
                </div>
              )}

              {/* Details */}
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>
                  {g.name}
                </p>
                <p style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>
                  {g.imageIds.length} slide{g.imageIds.length !== 1 ? 's' : ''}
                  {captions[g.id]?.caption ? ` · "${captions[g.id].caption.slice(0, 60)}${captions[g.id].caption.length > 60 ? '…' : ''}"` : ''}
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, color: '#374151', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Icon.Clock size={12} color="#4f46e5" /> {scheduledAt}
                  </span>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {selectedAccounts.map((accId) => {
                      const acc = accountById[accId];
                      if (!acc) return null;
                      return acc.avatar ? (
                        <img
                          key={accId}
                          src={acc.avatar}
                          alt={acc.name}
                          style={{ width: 22, height: 22, borderRadius: '50%', objectFit: 'cover', border: '1.5px solid #c7d2fe' }}
                        />
                      ) : (
                        <div
                          key={accId}
                          style={{
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            background: '#eef2ff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 10,
                            fontWeight: 700,
                            color: '#4f46e5',
                          }}
                        >
                          {(acc.name || acc.username || '?')[0].toUpperCase()}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Error */}
      {error && (
        <div
          style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 8,
            padding: '10px 14px',
            fontSize: 13,
            color: '#dc2626',
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Icon.AlertCircle size={14} />
          {error}
        </div>
      )}

      {/* Nav */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button onClick={onBack} style={BTN_OUTLINE} disabled={creating}>
          <Icon.ArrowLeft size={14} strokeWidth={2.5} /> Back
        </button>
        <button
          onClick={handleCreate}
          disabled={creating}
          style={creating ? BTN_DISABLED : BTN_PRIMARY}
        >
          {creating ? (
            <>
              <span
                className="spinner-border spinner-border-sm"
                style={{ width: 14, height: 14, borderWidth: 2 }}
                role="status"
              />
              Creating…
            </>
          ) : (
            <>
              <Icon.Send size={14} strokeWidth={2.5} />
              Create {groups.length} Post{groups.length !== 1 ? 's' : ''}
            </>
          )}
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   Main Wizard
───────────────────────────────────────── */
function CarouselCreator() {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [groups, setGroups] = useState([{ id: 'g0', name: 'Post 1', imageIds: [] }]);
  const [captions, setCaptions] = useState({});
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [instagramAccounts, setInstagramAccounts] = useState([]);
  const [startDate, setStartDate] = useState(todayISO());
  const [startTime, setStartTime] = useState('12:00');
  const [intervalHours, setIntervalHours] = useState(24);
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetch('https://api.schedpilot.com/wp-json/schedpilot/v1/get-settings', {
      credentials: 'include',
    })
      .then(r => r.json())
      .then(data => { if (data.timezone) setTimezone(data.timezone); })
      .catch(() => {});
  }, []);

  return (
    <MainStructure activeItem="creator-studio">
      <div className="container-fluid p-0">

        {/* Page Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <button
            onClick={() => navigate('/creator-studio')}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              display: 'flex',
              alignItems: 'center',
              color: '#64748b',
            }}
          >
            <Icon.ArrowLeft size={18} />
          </button>
          <div>
            <h1
              className="mb-0"
              style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.3px', color: '#0f172a' }}
            >
              Carousel Creator
            </h1>
            <p className="mb-0 small" style={{ color: '#64748b' }}>
              Create and schedule Instagram carousel posts
            </p>
          </div>
        </div>

        {/* Step indicator */}
        <StepIndicator current={step} />

        {/* Step content card */}
        <div style={CARD}>
          {step === 1 && (
            <StepUpload
              uploadedImages={uploadedImages}
              setUploadedImages={setUploadedImages}
              onNext={() => setStep(2)}
            />
          )}

          {step === 2 && (
            <StepGroup
              uploadedImages={uploadedImages}
              groups={groups}
              setGroups={setGroups}
              onBack={() => setStep(1)}
              onNext={() => setStep(3)}
            />
          )}

          {step === 3 && (
            <StepCaptions
              uploadedImages={uploadedImages}
              groups={groups}
              captions={captions}
              setCaptions={setCaptions}
              onBack={() => setStep(2)}
              onNext={() => setStep(4)}
            />
          )}

          {step === 4 && (
            <StepSchedule
              groups={groups}
              instagramAccounts={instagramAccounts}
              setInstagramAccounts={setInstagramAccounts}
              selectedAccounts={selectedAccounts}
              setSelectedAccounts={setSelectedAccounts}
              startDate={startDate}
              setStartDate={setStartDate}
              startTime={startTime}
              setStartTime={setStartTime}
              intervalHours={intervalHours}
              setIntervalHours={setIntervalHours}
              timezone={timezone}
              setTimezone={setTimezone}
              onBack={() => setStep(3)}
              onNext={() => setStep(5)}
            />
          )}

          {step === 5 && (
            <StepReview
              uploadedImages={uploadedImages}
              groups={groups}
              captions={captions}
              selectedAccounts={selectedAccounts}
              instagramAccounts={instagramAccounts}
              startDate={startDate}
              startTime={startTime}
              intervalHours={intervalHours}
              timezone={timezone}
              creating={creating}
              setCreating={setCreating}
              onBack={() => setStep(4)}
            />
          )}
        </div>
      </div>
    </MainStructure>
  );
}

export default CarouselCreator;
